import { BodyLarge, BoldSpan, Card, FlexCenter, FlexMdAlignCenter } from 'components/Elements';
import { ActivityType } from 'models/Activity';
import styled from 'styled-components';
import { breakpoints, sizes, theme } from 'theme';
import strings from 'strings/strings.json';
import VideoChatIcon from 'assets/icons/pathway/VideoChatIcon';
import QuestionnaireIcon from 'assets/icons/pathway/QuestionnaireIcon';
import TaskIcon from 'assets/icons/pathway/TaskIcon';
import { IconSize } from 'assets/icons/icon-sizes';
import { LearnerCourseContext } from 'contextProviders/LearnerCourseContext';
import { useContext } from 'react';
import ArrowRightIcon from 'assets/icons/controls/ArrowRightIcon';
import DurationLabel from 'components/DurationLabel';
import Button from 'components/buttons/Button';

export default function NextActivityComponent(): JSX.Element {
    const { setStep, learnerCourse } = useContext(LearnerCourseContext);
    const { modules } = learnerCourse!;
    const courseIsComplete = modules.flatMap((module) => module.activities).every((activity) => activity.completed);

    if (courseIsComplete) {
        return <></>;
    }

    const firstIncompleteModule = modules
        .filter((module) => module.activities.some((activity) => !activity.completed))
        .sort((moduleA, moduleB) => moduleA.order - moduleB.order)[0];

    const nextActivity = firstIncompleteModule.activities
        .filter((activity) => !activity.completed)
        .sort((activityA, activityB) => activityA.order - activityB.order)[0];

    if (!nextActivity.title) {
        return <></>;
    }

    let icon = <></>;

    switch (nextActivity.type) {
        case ActivityType.LIVE_TRAINING:
            icon = <VideoChatIcon size={IconSize.XLARGE} />;
            break;
        case ActivityType.QUESTIONNAIRE:
            icon = <QuestionnaireIcon size={IconSize.XLARGE} />;
            break;
        case ActivityType.TASK:
            icon = <TaskIcon size={IconSize.XLARGE} />;
            break;
        default:
            return <></>;
    }

    return (
        <NextActivityComponentCard>
            <TitleAndIconContainer>
                <IconContainer>{icon}</IconContainer>

                <ActivityTitle>
                    <BodyLargeColumn>
                        <span>{strings.learnerCoursePage.nextActivity.text}</span>
                        <BoldSpan>{nextActivity.title}</BoldSpan>
                        {nextActivity.durationInMins && (
                            <DurationLabel durationInMins={nextActivity.durationInMins} hideTitle />
                        )}
                    </BodyLargeColumn>
                </ActivityTitle>
            </TitleAndIconContainer>

            <ContinueButtonContainer>
                <Button
                    title={strings.learnerCoursePage.nextActivity.button.title}
                    aria={`${strings.learnerCoursePage.nextActivity.button.aria}: ${nextActivity.title}`}
                    onClick={() => {
                        setStep({ id: nextActivity.activityStatusId, stepType: 'Activity' });
                    }}
                    icon={<ArrowRightIcon />}
                    iconOnRight
                    colour='PRIMARY'
                />
            </ContinueButtonContainer>
        </NextActivityComponentCard>
    );
}

const ActivityTitle = styled.div`
    width: 100%;
`;

const BodyLargeColumn = styled(BodyLarge)`
    display: flex;
    flex-direction: column;
    gap: ${sizes.spacingXs};
`;

const TitleAndIconContainer = styled(FlexMdAlignCenter)`
    width: 100%;
`;

const IconContainer = styled(FlexCenter)`
    min-width: 64px;
    min-height: 64px;
    background-color: ${theme.iconBackgroundColour};
    border-radius: ${sizes.borderRadiusLg};
`;

const NextActivityComponentCard = styled(Card)`
    display: flex;
    gap: ${sizes.spacingMd};
    align-items: center;
    max-width: ${sizes.learnerCourseContentMaxWidth};

    @media (max-width: ${breakpoints.md}) {
        width: 100%;
    }

    @media (max-width: ${breakpoints.sm}) {
        flex-direction: column;
    }
`;

const ContinueButtonContainer = styled.div`
    display: grid;
    justify-items: flex-end;

    @media (max-width: ${breakpoints.sm}) {
        justify-items: unset;
        width: 100%;
    }
`;
